'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import {
  ShieldCheck,
  Building2,
  CreditCard,
  User,
  CheckCircle2,
  AlertCircle,
  ChevronRight,
  Loader2,
  BadgeCheck,
  Lock,
  FileText,
  Info,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import DirectoryTopbar from '@/app/loan-aggregator-directory/components/DirectoryTopbar';
import { createClient } from '@/lib/supabase/client';
import { toast } from 'sonner';

// ─── Types ────────────────────────────────────────────────────────────────────

type KycStep = 'bank' | 'pan' | 'identity' | 'done';

interface KycRecord {
  id: string;
  status: 'pending' | 'verified' | 'rejected';
  bank_account_number: string;
  bank_ifsc_code: string;
  bank_account_holder_name: string;
  bank_name: string;
  pan_number: string;
  full_name: string;
  date_of_birth: string;
  address: string;
  verified_at: string | null;
}

interface BankForm {
  account_holder_name: string;
  account_number: string;
  confirm_account_number: string;
  ifsc_code: string;
  bank_name: string;
}

interface PanForm {
  pan_number: string;
  name_on_pan: string;
}

interface IdentityForm {
  full_name: string;
  date_of_birth: string;
  address: string;
  declaration: boolean;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function validatePAN(pan: string): boolean {
  return /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(pan.toUpperCase());
}

function validateIFSC(ifsc: string): boolean {
  return /^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifsc.toUpperCase());
}

// ─── Step Indicator ───────────────────────────────────────────────────────────

const STEPS: { key: KycStep; label: string; icon: React.ReactNode }[] = [
  { key: 'bank', label: 'Bank Account', icon: <Building2 size={16} /> },
  { key: 'pan', label: 'PAN Card', icon: <CreditCard size={16} /> },
  { key: 'identity', label: 'Identity', icon: <User size={16} /> },
];

function StepIndicator({ current }: { current: KycStep }) {
  const stepKeys: KycStep[] = ['bank', 'pan', 'identity', 'done'];
  const currentIdx = stepKeys.indexOf(current);

  return (
    <div className="flex items-center gap-0 mb-8">
      {STEPS.map((step, idx) => {
        const isCompleted = idx < currentIdx;
        const isActive = step.key === current;
        return (
          <React.Fragment key={step.key}>
            <div className="flex flex-col items-center gap-1.5 flex-shrink-0">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${
                  isCompleted
                    ? 'bg-green-500 border-green-500 text-white'
                    : isActive
                    ? 'bg-primary border-primary text-white' :'bg-muted border-border text-muted-foreground'
                }`}
              >
                {isCompleted ? <CheckCircle2 size={18} /> : step.icon}
              </div>
              <span
                className={`text-xs font-semibold whitespace-nowrap ${
                  isActive ? 'text-primary' : isCompleted ? 'text-green-600' : 'text-muted-foreground'
                }`}
              >
                {step.label}
              </span>
            </div>
            {idx < STEPS.length - 1 && (
              <div
                className={`flex-1 h-0.5 mx-2 mb-5 rounded-full transition-all ${
                  idx < currentIdx ? 'bg-green-400' : 'bg-border'
                }`}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─── Bank Account Step ────────────────────────────────────────────────────────

function BankStep({
  onNext,
  submitting,
}: {
  onNext: (data: BankForm) => void;
  submitting: boolean;
}) {
  const [form, setForm] = useState<BankForm>({
    account_holder_name: '',
    account_number: '',
    confirm_account_number: '',
    ifsc_code: '',
    bank_name: '',
  });
  const [errors, setErrors] = useState<Partial<BankForm>>({});

  const set = (k: keyof BankForm, v: string) => {
    setForm((p) => ({ ...p, [k]: v }));
    setErrors((p) => ({ ...p, [k]: undefined }));
  };

  const validate = () => {
    const e: Partial<BankForm> = {};
    if (!form.account_holder_name.trim()) e.account_holder_name = 'Account holder name is required';
    if (!form.account_number.trim() || !/^\d{9,18}$/.test(form.account_number))
      e.account_number = 'Enter a valid account number (9–18 digits)';
    if (form.confirm_account_number !== form.account_number)
      e.confirm_account_number = 'Account numbers do not match';
    if (!validateIFSC(form.ifsc_code)) e.ifsc_code = 'Enter a valid IFSC code (e.g. SBIN0001234)';
    if (!form.bank_name.trim()) e.bank_name = 'Bank name is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) onNext(form);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-xl">
        <Info size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
        <p className="text-sm text-blue-700">
          Your bank account will be used for all future payout transfers. Ensure the account is in your name.
        </p>
      </div>

      <div>
        <label className="block text-sm font-semibold text-foreground mb-1.5">Account Holder Name</label>
        <input
          type="text"
          value={form.account_holder_name}
          onChange={(e) => set('account_holder_name', e.target.value)}
          placeholder="As per bank records"
          className={`w-full px-4 py-2.5 rounded-xl border text-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all ${
            errors.account_holder_name ? 'border-red-400' : 'border-border'
          }`}
        />
        {errors.account_holder_name && (
          <p className="text-xs text-red-500 mt-1">{errors.account_holder_name}</p>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-semibold text-foreground mb-1.5">Account Number</label>
          <input
            type="text"
            value={form.account_number}
            onChange={(e) => set('account_number', e.target.value.replace(/\D/g, ''))}
            placeholder="Enter account number"
            className={`w-full px-4 py-2.5 rounded-xl border text-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all ${
              errors.account_number ? 'border-red-400' : 'border-border'
            }`}
          />
          {errors.account_number && (
            <p className="text-xs text-red-500 mt-1">{errors.account_number}</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-semibold text-foreground mb-1.5">Confirm Account Number</label>
          <input
            type="text"
            value={form.confirm_account_number}
            onChange={(e) => set('confirm_account_number', e.target.value.replace(/\D/g, ''))}
            placeholder="Re-enter account number"
            className={`w-full px-4 py-2.5 rounded-xl border text-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all ${
              errors.confirm_account_number ? 'border-red-400' : 'border-border'
            }`}
          />
          {errors.confirm_account_number && (
            <p className="text-xs text-red-500 mt-1">{errors.confirm_account_number}</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-semibold text-foreground mb-1.5">IFSC Code</label>
          <input
            type="text"
            value={form.ifsc_code}
            onChange={(e) => set('ifsc_code', e.target.value.toUpperCase())}
            placeholder="e.g. SBIN0001234"
            maxLength={11}
            className={`w-full px-4 py-2.5 rounded-xl border text-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all font-mono ${
              errors.ifsc_code ? 'border-red-400' : 'border-border'
            }`}
          />
          {errors.ifsc_code && (
            <p className="text-xs text-red-500 mt-1">{errors.ifsc_code}</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-semibold text-foreground mb-1.5">Bank Name</label>
          <input
            type="text"
            value={form.bank_name}
            onChange={(e) => set('bank_name', e.target.value)}
            placeholder="e.g. State Bank of India"
            className={`w-full px-4 py-2.5 rounded-xl border text-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all ${
              errors.bank_name ? 'border-red-400' : 'border-border'
            }`}
          />
          {errors.bank_name && (
            <p className="text-xs text-red-500 mt-1">{errors.bank_name}</p>
          )}
        </div>
      </div>

      <button
        type="submit"
        disabled={submitting}
        className="w-full flex items-center justify-center gap-2 py-3 bg-primary text-white rounded-xl font-semibold text-sm hover:bg-blue-700 transition-colors disabled:opacity-60"
      >
        {submitting ? <Loader2 size={16} className="animate-spin" /> : null}
        Continue to PAN Verification
        <ChevronRight size={16} />
      </button>
    </form>
  );
}

// ─── PAN Step ─────────────────────────────────────────────────────────────────

function PanStep({
  onNext,
  submitting,
}: {
  onNext: (data: PanForm) => void;
  submitting: boolean;
}) {
  const [form, setForm] = useState<PanForm>({ pan_number: '', name_on_pan: '' });
  const [errors, setErrors] = useState<Partial<PanForm>>({});

  const set = (k: keyof PanForm, v: string) => {
    setForm((p) => ({ ...p, [k]: v }));
    setErrors((p) => ({ ...p, [k]: undefined }));
  };

  const validate = () => {
    const e: Partial<PanForm> = {};
    if (!validatePAN(form.pan_number)) e.pan_number = 'Enter a valid PAN (e.g. ABCDE1234F)';
    if (!form.name_on_pan.trim()) e.name_on_pan = 'Name as on PAN card is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) onNext(form);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
        <Lock size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
        <p className="text-sm text-amber-700">
          Your PAN details are encrypted and stored securely. This is required by RBI guidelines for payout processing.
        </p>
      </div>

      <div>
        <label className="block text-sm font-semibold text-foreground mb-1.5">PAN Number</label>
        <input
          type="text"
          value={form.pan_number}
          onChange={(e) => set('pan_number', e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
          placeholder="e.g. ABCDE1234F"
          maxLength={10}
          className={`w-full px-4 py-2.5 rounded-xl border text-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all font-mono tracking-widest ${
            errors.pan_number ? 'border-red-400' : 'border-border'
          }`}
        />
        {errors.pan_number && (
          <p className="text-xs text-red-500 mt-1">{errors.pan_number}</p>
        )}
        <p className="text-xs text-muted-foreground mt-1">Format: 5 letters + 4 digits + 1 letter (e.g. ABCDE1234F)</p>
      </div>

      <div>
        <label className="block text-sm font-semibold text-foreground mb-1.5">Name as on PAN Card</label>
        <input
          type="text"
          value={form.name_on_pan}
          onChange={(e) => set('name_on_pan', e.target.value.toUpperCase())}
          placeholder="Full name exactly as printed on PAN"
          className={`w-full px-4 py-2.5 rounded-xl border text-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all ${
            errors.name_on_pan ? 'border-red-400' : 'border-border'
          }`}
        />
        {errors.name_on_pan && (
          <p className="text-xs text-red-500 mt-1">{errors.name_on_pan}</p>
        )}
      </div>

      <button
        type="submit"
        disabled={submitting}
        className="w-full flex items-center justify-center gap-2 py-3 bg-primary text-white rounded-xl font-semibold text-sm hover:bg-blue-700 transition-colors disabled:opacity-60"
      >
        {submitting ? <Loader2 size={16} className="animate-spin" /> : null}
        Continue to Identity Confirmation
        <ChevronRight size={16} />
      </button>
    </form>
  );
}

// ─── Identity Step ────────────────────────────────────────────────────────────

function IdentityStep({
  onSubmit,
  submitting,
}: {
  onSubmit: (data: IdentityForm) => void;
  submitting: boolean;
}) {
  const [form, setForm] = useState<IdentityForm>({
    full_name: '',
    date_of_birth: '',
    address: '',
    declaration: false,
  });
  const [errors, setErrors] = useState<Partial<Record<keyof IdentityForm, string>>>({});

  const set = (k: keyof IdentityForm, v: string | boolean) => {
    setForm((p) => ({ ...p, [k]: v }));
    setErrors((p) => ({ ...p, [k]: undefined }));
  };

  const validate = () => {
    const e: Partial<Record<keyof IdentityForm, string>> = {};
    if (!form.full_name.trim()) e.full_name = 'Full legal name is required';
    if (!form.date_of_birth) e.date_of_birth = 'Date of birth is required';
    else {
      const dob = new Date(form.date_of_birth);
      const age = (Date.now() - dob.getTime()) / (1000 * 60 * 60 * 24 * 365.25);
      if (age < 18) e.date_of_birth = 'You must be at least 18 years old';
    }
    if (!form.address.trim() || form.address.trim().length < 20)
      e.address = 'Please enter your complete address (min 20 characters)';
    if (!form.declaration) e.declaration = 'You must accept the declaration to proceed';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) onSubmit(form);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-xl">
        <FileText size={16} className="text-green-600 mt-0.5 flex-shrink-0" />
        <p className="text-sm text-green-700">
          This is the final step. Confirm your identity details to complete KYC and unlock payout eligibility.
        </p>
      </div>

      <div>
        <label className="block text-sm font-semibold text-foreground mb-1.5">Full Legal Name</label>
        <input
          type="text"
          value={form.full_name}
          onChange={(e) => set('full_name', e.target.value)}
          placeholder="As per government-issued ID"
          className={`w-full px-4 py-2.5 rounded-xl border text-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all ${
            errors.full_name ? 'border-red-400' : 'border-border'
          }`}
        />
        {errors.full_name && <p className="text-xs text-red-500 mt-1">{errors.full_name}</p>}
      </div>

      <div>
        <label className="block text-sm font-semibold text-foreground mb-1.5">Date of Birth</label>
        <input
          type="date"
          value={form.date_of_birth}
          onChange={(e) => set('date_of_birth', e.target.value)}
          max={new Date(Date.now() - 18 * 365.25 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
          className={`w-full px-4 py-2.5 rounded-xl border text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all ${
            errors.date_of_birth ? 'border-red-400' : 'border-border'
          }`}
        />
        {errors.date_of_birth && <p className="text-xs text-red-500 mt-1">{errors.date_of_birth}</p>}
      </div>

      <div>
        <label className="block text-sm font-semibold text-foreground mb-1.5">Residential Address</label>
        <textarea
          value={form.address}
          onChange={(e) => set('address', e.target.value)}
          placeholder="Full address including city, state, and PIN code"
          rows={3}
          className={`w-full px-4 py-2.5 rounded-xl border text-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all resize-none ${
            errors.address ? 'border-red-400' : 'border-border'
          }`}
        />
        {errors.address && <p className="text-xs text-red-500 mt-1">{errors.address}</p>}
      </div>

      <div
        className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-all ${
          form.declaration ? 'bg-primary/5 border-primary/30' : 'bg-muted/40 border-border'
        } ${errors.declaration ? 'border-red-400' : ''}`}
        onClick={() => set('declaration', !form.declaration)}
      >
        <div
          className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 mt-0.5 border-2 transition-all ${
            form.declaration ? 'bg-primary border-primary' : 'border-border bg-background'
          }`}
        >
          {form.declaration && <CheckCircle2 size={12} className="text-white" />}
        </div>
        <p className="text-sm text-foreground leading-relaxed">
          I declare that the information provided is accurate and complete. I consent to verification of my details for KYC compliance as per applicable regulations.
        </p>
      </div>
      {errors.declaration && <p className="text-xs text-red-500 -mt-3">{errors.declaration}</p>}

      <button
        type="submit"
        disabled={submitting}
        className="w-full flex items-center justify-center gap-2 py-3 bg-green-600 text-white rounded-xl font-semibold text-sm hover:bg-green-700 transition-colors disabled:opacity-60"
      >
        {submitting ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />}
        Submit KYC Verification
      </button>
    </form>
  );
}

// ─── Success View ─────────────────────────────────────────────────────────────

function SuccessView({ onGoToPayouts }: { onGoToPayouts: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
      <div className="w-20 h-20 rounded-full bg-green-50 border-2 border-green-200 flex items-center justify-center mb-6">
        <BadgeCheck size={40} className="text-green-500" />
      </div>
      <h2 className="text-2xl font-extrabold text-foreground mb-2">KYC Submitted!</h2>
      <p className="text-muted-foreground text-sm mb-1 max-w-sm">
        Your KYC details have been submitted for verification. This typically takes 1–2 business days.
      </p>
      <p className="text-xs text-muted-foreground mb-8 max-w-sm">
        Once verified, you'll be eligible to request payouts. You'll receive a notification when your KYC is approved.
      </p>
      <div className="flex flex-col sm:flex-row gap-3 w-full max-w-xs">
        <button
          onClick={onGoToPayouts}
          className="flex-1 px-6 py-2.5 bg-primary text-white rounded-xl text-sm font-semibold hover:bg-blue-700 transition-colors"
        >
          Go to Payouts
        </button>
        <button
          onClick={() => window.history.back()}
          className="flex-1 px-6 py-2.5 bg-muted text-foreground rounded-xl text-sm font-semibold hover:bg-muted/80 transition-colors"
        >
          Back
        </button>
      </div>
    </div>
  );
}

// ─── Already Verified Banner ──────────────────────────────────────────────────

function AlreadyVerifiedBanner({
  kyc,
  onGoToPayouts,
}: {
  kyc: KycRecord;
  onGoToPayouts: () => void;
}) {
  const statusConfig = {
    pending: {
      icon: <AlertCircle size={20} className="text-amber-500" />,
      bg: 'bg-amber-50 border-amber-200',
      title: 'KYC Under Review',
      desc: 'Your KYC has been submitted and is being reviewed. Payouts will be enabled once verified.',
    },
    verified: {
      icon: <BadgeCheck size={20} className="text-green-500" />,
      bg: 'bg-green-50 border-green-200',
      title: 'KYC Verified',
      desc: 'Your identity has been verified. You are eligible to request payouts.',
    },
    rejected: {
      icon: <AlertCircle size={20} className="text-red-500" />,
      bg: 'bg-red-50 border-red-200',
      title: 'KYC Rejected',
      desc: 'Your KYC was rejected. Please re-submit with correct details.',
    },
  };

  const cfg = statusConfig[kyc.status];

  return (
    <div className="space-y-6">
      <div className={`flex items-start gap-4 p-5 rounded-2xl border ${cfg.bg}`}>
        <div className="flex-shrink-0 mt-0.5">{cfg.icon}</div>
        <div>
          <p className="font-bold text-foreground">{cfg.title}</p>
          <p className="text-sm text-muted-foreground mt-0.5">{cfg.desc}</p>
        </div>
      </div>

      {kyc.status !== 'rejected' && (
        <div className="bg-card border border-border rounded-2xl divide-y divide-border">
          <div className="px-5 py-4">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Submitted Details</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { label: 'Account Holder', value: kyc.bank_account_holder_name },
                { label: 'Bank', value: kyc.bank_name },
                { label: 'Account Number', value: `••••${kyc.bank_account_number?.slice(-4)}` },
                { label: 'IFSC Code', value: kyc.bank_ifsc_code },
                { label: 'PAN Number', value: `${kyc.pan_number?.slice(0, 3)}•••••${kyc.pan_number?.slice(-2)}` },
                { label: 'Full Name', value: kyc.full_name },
              ].map((item) => (
                <div key={item.label}>
                  <p className="text-xs text-muted-foreground">{item.label}</p>
                  <p className="text-sm font-semibold text-foreground">{item.value || '—'}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <button
        onClick={onGoToPayouts}
        className="w-full py-3 bg-primary text-white rounded-xl font-semibold text-sm hover:bg-blue-700 transition-colors"
      >
        Go to Payouts
      </button>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function KycVerificationPage() {
  const router = useRouter();
  const { user, loading: authLoading } = useAuth();
  const supabase = createClient();

  const [step, setStep] = useState<KycStep>('bank');
  const [submitting, setSubmitting] = useState(false);
  const [loadingKyc, setLoadingKyc] = useState(true);
  const [existingKyc, setExistingKyc] = useState<KycRecord | null>(null);

  // Collected form data across steps
  const [bankData, setBankData] = useState<BankForm | null>(null);
  const [panData, setPanData] = useState<PanForm | null>(null);

  // ── Auth guard ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!authLoading && !user) {
      router.push('/');
    }
  }, [user, authLoading, router]);

  // ── Load existing KYC ───────────────────────────────────────────────────────
  const loadKyc = useCallback(async () => {
    if (!user) return;
    setLoadingKyc(true);
    try {
      const { data } = await supabase
        .from('kyc_verifications')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();
      setExistingKyc(data ?? null);
    } catch {
      // No KYC found — fresh start
    } finally {
      setLoadingKyc(false);
    }
  }, [user, supabase]);

  useEffect(() => {
    if (user) loadKyc();
  }, [user, loadKyc]);

  // ── Step handlers ───────────────────────────────────────────────────────────
  const handleBankNext = (data: BankForm) => {
    setBankData(data);
    setStep('pan');
  };

  const handlePanNext = (data: PanForm) => {
    setPanData(data);
    setStep('identity');
  };

  const handleIdentitySubmit = async (data: IdentityForm) => {
    if (!user || !bankData || !panData) return;
    setSubmitting(true);
    try {
      const { error } = await supabase.from('kyc_verifications').upsert({
        user_id: user.id,
        bank_account_holder_name: bankData.account_holder_name,
        bank_account_number: bankData.account_number,
        bank_ifsc_code: bankData.ifsc_code.toUpperCase(),
        bank_name: bankData.bank_name,
        pan_number: panData.pan_number.toUpperCase(),
        name_on_pan: panData.name_on_pan,
        full_name: data.full_name,
        date_of_birth: data.date_of_birth,
        address: data.address,
        status: 'pending',
        submitted_at: new Date().toISOString(),
      });

      if (error) throw error;

      setStep('done');
      toast.success('KYC submitted successfully!');
    } catch (err) {
      toast.error('Failed to submit KYC. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Loading state ───────────────────────────────────────────────────────────
  if (authLoading || loadingKyc) {
    return (
      <div className="min-h-screen bg-background">
        <DirectoryTopbar />
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 size={28} className="animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <DirectoryTopbar />

      <main className="max-w-2xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <ShieldCheck size={20} className="text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-extrabold text-foreground">KYC Verification</h1>
              <p className="text-sm text-muted-foreground">Required before your first payout request</p>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-2xl shadow-sm p-6 md:p-8">
          {/* Existing KYC — show status */}
          {existingKyc && step !== 'done' ? (
            <AlreadyVerifiedBanner
              kyc={existingKyc}
              onGoToPayouts={() => router.push('/payouts')}
            />
          ) : step === 'done' ? (
            <SuccessView onGoToPayouts={() => router.push('/payouts')} />
          ) : (
            <>
              <StepIndicator current={step} />
              {step === 'bank' && (
                <BankStep onNext={handleBankNext} submitting={submitting} />
              )}
              {step === 'pan' && (
                <PanStep onNext={handlePanNext} submitting={submitting} />
              )}
              {step === 'identity' && (
                <IdentityStep onSubmit={handleIdentitySubmit} submitting={submitting} />
              )}
            </>
          )}
        </div>

        {/* Trust indicators */}
        {step !== 'done' && !existingKyc && (
          <div className="mt-6 flex flex-wrap items-center justify-center gap-4 text-xs text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <Lock size={12} />
              256-bit encrypted
            </span>
            <span className="flex items-center gap-1.5">
              <ShieldCheck size={12} />
              RBI compliant
            </span>
            <span className="flex items-center gap-1.5">
              <BadgeCheck size={12} />
              Secure storage
            </span>
          </div>
        )}
      </main>
    </div>
  );
}
